import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationsListSkeleton } from './NotificationsList.skeleton';
import { useNotificationsList } from './useNotificationsList';

import { DateFormat } from '@speeki/frontend-utils';
import { Typography } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { useResponsive } from '@utils/hooks/useResponsive';

import {
    EmptyResultsWrapper,
    EmptyResultText,
    InfiniteScrollWrapper,
    ListItemDate,
    ListItemDescription,
    ListItemHeader,
    ListItemTitle,
    MarkAsReadButton,
    NotificationListItem,
    NotificationsHeader,
    NotificationsListWrapper,
    NotificationsWrapper,
} from './NotificationsList.styled';

interface NotificationsListProps {
    onClose: () => void;
}

export const NotificationsList = ({ onClose }: NotificationsListProps) => {
    const { t } = useTranslation();
    const isXs = useResponsive('xs');

    const {
        handleNotificationOpen,
        hasNextPage,
        infiniteRef,
        loading,
        markAllAsRead,
        notifications,
        notificationsLength,
        rootRef,
    } = useNotificationsList({ onClose });

    const notificationList = useMemo(
        () =>
            notifications?.map(
                ({
                    node: {
                        createdAt,
                        payload: {
                            data: { extra, refUuid },
                            notification: { body, title },
                        },
                        readNotification,
                        type,
                        uuid,
                    },
                }) => (
                    <NotificationListItem
                        key={uuid}
                        onClick={() =>
                            handleNotificationOpen(
                                uuid,
                                extra?.disclosureUuid,
                                refUuid,
                                type,
                                !!readNotification?.length,
                            )
                        }
                    >
                        <ListItemHeader>
                            <ListItemTitle unread={!readNotification?.length}>
                                {title}
                            </ListItemTitle>
                            <ListItemDate>
                                {DateFormat.standard(createdAt)}
                            </ListItemDate>
                        </ListItemHeader>
                        {!isXs && (
                            <ListItemDescription>{body}</ListItemDescription>
                        )}
                    </NotificationListItem>
                ),
            ),
        [notifications, isXs],
    );

    const renderNotifications = () => {
        if (!notificationsLength)
            return (
                <EmptyResultsWrapper>
                    <IconManager
                        icon={'SvgNotificationEmptyState'}
                        size={24}
                        themeColors={{
                            default: {
                                'color-1': 'text.assistive',
                            },
                        }}
                    />
                    <EmptyResultText>
                        {t('NO_NOTIFICATIONS_TEXT')}
                    </EmptyResultText>
                </EmptyResultsWrapper>
            );

        return (
            <>
                {notificationList}
                {hasNextPage && (
                    <InfiniteScrollWrapper ref={infiniteRef}>
                        <NotificationsListSkeleton />
                    </InfiniteScrollWrapper>
                )}
            </>
        );
    };

    return (
        <NotificationsListWrapper autoHeight={!loading && !notificationsLength}>
            <NotificationsHeader>
                <Typography.TextH3_24_34>
                    {t('NOTIFICATIONS')}
                </Typography.TextH3_24_34>
                {!!notificationsLength && (
                    <MarkAsReadButton
                        onClick={() => markAllAsRead()}
                        testId="ZFlyLpZ"
                    >
                        {t('MARK_ALL_AS_READ_TEXT')}
                    </MarkAsReadButton>
                )}
            </NotificationsHeader>
            <NotificationsWrapper ref={rootRef}>
                {!loading ? (
                    renderNotifications()
                ) : (
                    <NotificationsListSkeleton />
                )}
            </NotificationsWrapper>
        </NotificationsListWrapper>
    );
};
